import { createContext, useContext } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { Resolver, useForm } from 'react-hook-form'
import { z } from 'zod'

import authCookieManager from '@App/cookies/auth_cookie_manager'
import { useAppDispatch, useAppSelector } from '@App/hooks'
import { makeSpotOrderRequest } from '@App/redux/slices/sockets/FrontServiceConnectionSlice'
import { currentUser } from '@App/redux/slices/user/selectors'

import { selectMarketByCurrencies } from "@Redux/slices/config/config.selectors"
import { Order, Trade } from "@Redux/slices/trade/trades/types"

import { preprocessNumberByFieldPricision, roundNumber } from '../../../../model'

const createCancelFormSchema = (amount_precision: number) => {
  return z
    .object({
      amount: z.number(),
      targetAmount: z.preprocess(
        preprocessNumberByFieldPricision('amount', amount_precision),
        z.coerce.number().min(1 / Math.pow(10, amount_precision), `The size is ${1 / Math.pow(10, amount_precision)}`),
      ),
    })
    .superRefine((data, ctx) => {
      if (data.targetAmount > data.amount) {
        ctx.addIssue({
          input: data,
          code: 'too_big',
          origin: 'number',
          maximum: data.amount,
          message: `Target amount cannot be greater than ${data.amount}`,
          path: ['targetAmount'],
        })
      }
    })
}
type CloseOrderForm = z.infer<ReturnType<typeof createCancelFormSchema>>

export const useCloseOrder = (trade: Trade|Order, rate: number) => {
  const form = useContext(CancelOrderContext)
  if (!form) {
    throw new Error('close order context not initialized')
  }

  const dispatch = useAppDispatch()
  const access_token = authCookieManager.getAccessToken()
  const user = useAppSelector(currentUser)

  const closeOrder = form.handleSubmit(async (data: CloseOrderForm) => {
    await dispatch(
      makeSpotOrderRequest({
        market_id: trade.market.id,
        token: access_token || '',
        user_id: user.user_id,
        amount: data.targetAmount,
        action: trade.action_type,
        wallet_id: trade.wallet_id,
        rate: rate,
        type: 'close',
        trade_id: trade.id,
      }),
    )
  })
  const targetAmount = form.watch('targetAmount')
  return { closeOrder, targetAmount }
}
type UseFormReturnValue = Pick<ReturnType<typeof useForm<CloseOrderForm>>, 'register' | 'watch' | 'handleSubmit'>
type CancelOrderContextType = { setTargetAmountValue: (val: number) => void; error?: string } & UseFormReturnValue
const CancelOrderContext = createContext<CancelOrderContextType | null>(null)

export const CancelTradeProvider = ({ children, order }: { children: React.ReactNode; order: Trade|Order }) => {
  const market = useAppSelector((state) => selectMarketByCurrencies(state, order.market.source, order.market.target))

  const form = useForm<CloseOrderForm>({
    resolver: zodResolver(createCancelFormSchema(market?.price_precision ?? 2)) as Resolver<CloseOrderForm>,
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: {
      amount: roundNumber(order.quantity, market?.amount_precision ?? 2),
      targetAmount: roundNumber(order.quantity, market?.amount_precision ?? 2),
    },
  })
  const setTargetAmountValue = (val: number) => {
    form.setValue('targetAmount', val, { shouldDirty: true, shouldValidate: true })
  }

  return (
    <CancelOrderContext.Provider
      value={{
        register: form.register,
        error: form.formState.errors['targetAmount']?.message,
        setTargetAmountValue,
        watch: form.watch,
        handleSubmit: form.handleSubmit,
      }}
    >
      {children}
    </CancelOrderContext.Provider>
  )
}

export const useCancelOrderForm = (pnl: number) => {
  const form = useContext(CancelOrderContext)
  if (!form) {
    throw new Error('close order context not initialized')
  }

  const { register, error, setTargetAmountValue, watch } = form
  const amount = watch('amount')
  const targetAmount = watch('targetAmount')

  const currentPercentOfAmount = amount && amount !== 0 ? (targetAmount / amount) * 100 : 0

  const targetPnl = (pnl * currentPercentOfAmount) / 100
  const setTargetAmountByPercent = (percent: number) => {
    if (!amount || amount === 0) {
      setTargetAmountValue(0)
      return
    }

    const baseAmount = Math.abs(amount)

    const target = (baseAmount * percent) / 100

    setTargetAmountValue(target)
  }

  return {
    register,
    error,
    currentPercentOfAmount,
    targetPnl,
    setTargetAmountByPercent,
  }
}
